import React from "react";
import { Link, useLocation } from "react-router-dom";
import useIdentityCoach from "../../hooks/useIdentityCoach";
import styles from "../../styles/components/product/ProductAiOnboardingCta.module.css";

const ProductAiOnboardingCta = ({
  onStartSearch,
  onAskQuestion = null,
  identityRequired = false,
  lowConfidence = false,
  identityCompleteness = null,
  productName = null,
  categoryName = null,
}) => {
  const location = useLocation();
  const reason = identityRequired
    ? "identity_required"
    : lowConfidence
      ? "low_confidence"
      : null;

  const { coaching, loading } = useIdentityCoach({
    enabled: Boolean(reason),
    identityCompleteness,
    productName,
    categoryName,
    lowConfidence,
    reason: reason || "identity_required",
  });

  const showCoach = Boolean(reason);
  const progressPercent = coaching?.progressPercent ?? identityCompleteness?.percent;
  const showProgress =
    identityRequired &&
    progressPercent > 0 &&
    progressPercent < 100;

  const chatOnboardingPath = {
    pathname: "/onboarding/shopping-profile",
    search: "?mode=chat",
    state: {
      from: `${location.pathname}${location.search}`,
    },
  };

  const primaryAction =
    reason === "identity_required" ? (
      <Link to={chatOnboardingPath} className={styles.primaryBtn}>
        Chat to complete profile
      </Link>
    ) : reason === "low_confidence" ? (
      <button type="button" className={styles.primaryBtn} onClick={onStartSearch}>
        {coaching?.actionLabel || "Refine in chat"}
      </button>
    ) : (
      !identityRequired && (
        <button type="button" className={styles.primaryBtn} onClick={onStartSearch}>
          Start a quick search
        </button>
      )
    );

  const secondaryAction =
    reason === "identity_required" ? (
      <Link to="/onboarding/shopping-profile" className={styles.linkBtn}>
        Use quick form instead
      </Link>
    ) : reason === "low_confidence" ? null : (
      <Link to="/onboarding/shopping-profile" className={styles.linkBtn}>
        {coaching?.actionLabel || "Complete shopping profile"}
      </Link>
    );

  const handleQuestionClick = (question) => {
    if (!question) return;
    onAskQuestion?.(question);
  };

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>Want to know if this fits you?</h3>

      {showCoach && coaching?.headline ? (
        <p className={styles.subtitle}>{coaching.headline}</p>
      ) : (
        <p className={styles.subtitle}>
          {identityRequired
            ? "Complete your shopping profile to unlock a trustworthy match score."
            : "Tell us your budget and use case - we'll score this product for you. Use the chat below to ask Simpary directly."}
        </p>
      )}

      {showProgress && (
        <div className={styles.progressWrap}>
          <div className={styles.progressMeta}>
            <span>Profile completeness</span>
            <span>{progressPercent}%</span>
          </div>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {showCoach && coaching?.questions?.length > 0 && (
        <ul className={styles.questionList}>
          {coaching.questions.map((item) => (
            <li key={item.field} className={styles.questionItem}>
              <button
                type="button"
                className={styles.questionButton}
                onClick={() => handleQuestionClick(item.question)}
              >
                <p className={styles.questionText}>{item.question}</p>
                {item.why && <p className={styles.questionWhy}>{item.why}</p>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {loading && !coaching?.headline && (
        <p className={styles.loadingHint}>Personalizing guidance…</p>
      )}

      <div className={styles.actions}>
        {primaryAction}
        {secondaryAction}
        {reason === "low_confidence" && (
          <Link to={chatOnboardingPath} className={styles.linkBtn}>
            Complete shopping profile
          </Link>
        )}
      </div>
    </div>
  );
};

export default ProductAiOnboardingCta;
